import Image from 'next/image';
import { type Ref } from 'react';

import { pick } from 'lodash-es';

import { titleCase } from '@craft/lib/formatters';
import { AvatarDiscreteSize } from '@craft/ui/components/avatars';
import { ButtonBase, type ButtonBaseProps } from '@craft/ui/components/buttons';
import { CaretIcon } from '@craft/ui/components/icons';
import { Stack } from '@craft/ui/components/structural';
import { UserTile, type UserTileProps } from '@craft/ui/components/users';
import { mergeSx, stringifyMediaQueryObj } from '@craft/ui/util';

import { useUser } from '@/src/features/auth/hooks';
import { useUserAvatar } from '@/src/hooks/query/users/use-user-avatar';

/**
 * Defines the maximum width of the containing header bar that the `UserProfileButton` can expand to
 * before the user's avatar is the only thing shown (i.e. the user's name and organization info are
 * hidden).
 *
 * Value is defined in pixels.
 */
const AvatarOnlyBreakpoint = 380;

/**
 * The background color that is applied to the `UserProfileButton` when the dropdown is open or
 * when the button is in a focused or hovered state.  This color does not exist anywhere else
 * in the theme or application as it is specifically designed to provide a subtle but clear tint
 * to this specific button when it is rendered on top of the {@link CraftHeaderBar} which has a
 * background color of `#1a3558`.
 */
const InteractiveBackgroundColor = '#1b3a5e';

const CommonTextSx = {
  color: 'primary.contrastText',
  lineClamp: 1,
  truncate: true,
  whiteSpace: 'nowrap',
} as const satisfies UserTileProps['subTitleProps'];

export interface UserProfileButtonProps extends Omit<
  ButtonBaseProps<'button'>,
  'children' | 'component'
> {
  /**
   * Defines the background color of the {@link UserProfileButton} when it is in an interactive
   * state (hovered, focused, or when the dropdown is open).
   */
  readonly interactiveBackgroundColor?: string;
  readonly isOpen: boolean;
  readonly ref?: Ref<HTMLButtonElement>;
}

export const UserProfileButton = ({
  interactiveBackgroundColor,
  isOpen,
  ref,
  ...props
}: UserProfileButtonProps) => {
  const user = useUser();
  const { data: avatar } = useUserAvatar(user.id, AvatarDiscreteSize.Large, {
    includeBlur: true,
  });

  return (
    <ButtonBase
      color='inherit'
      id='current-user-control-menu-btn'
      {...props}
      aria-label='open current user display menu'
      component='button'
      ref={ref}
      sx={mergeSx(
        {
          '&:hover,&:focus-visible,&:focus-within': {
            backgroundColor: interactiveBackgroundColor ?? InteractiveBackgroundColor,
          },
          backgroundColor: isOpen
            ? (interactiveBackgroundColor ?? InteractiveBackgroundColor)
            : 'transparent',
          padding: 1,
          textAlign: 'left',
        },
        props.sx,
      )}
    >
      <Stack
        alignItems='center'
        direction='row'
        flexShrink={0}
        gap={0.5}
        maxWidth={`min(${AvatarOnlyBreakpoint}px, 100%)`}
        sx={{ overflow: 'hidden' }}
      >
        <UserTile
          {...pick(user, ['firstName', 'lastName'])}
          avatar={avatar ?? null}
          imageComponent={Image}
          subTitle={
            `${titleCase(user.selectedOrganizationRole.role, true)} • ` +
            `${titleCase(user.selectedOrganizationRole.organization.name, false)}`
          }
          subTitleProps={{ ...CommonTextSx, fontSize: '0.875rem', fontWeight: 500 }}
          sx={{ maxWidth: '100%', width: 'auto' }}
          titleProps={{ ...CommonTextSx, fontSize: '1.125rem', fontWeight: 600 }}
        >
          {({ children }) => (
            <Stack
              sx={{
                [stringifyMediaQueryObj(
                  { width: { max: AvatarOnlyBreakpoint } },
                  { prefix: '@container app-header' },
                )]: {
                  display: 'none',
                },
              }}
            >
              {children}
            </Stack>
          )}
        </UserTile>
        <Stack
          alignItems='center'
          direction='row'
          flexShrink={0}
          height='100%'
          justifyContent='center'
          width='auto'
        >
          <CaretIcon color='primary.contrastText' direction={isOpen ? 'up' : 'down'} size={24} />
        </Stack>
      </Stack>
    </ButtonBase>
  );
};
